import { Container } from '@mui/material';
import { ODSCopyPaste } from '@telekom-ods/react-icons';
import { useState, useEffect, type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useParams, useNavigate, Link, useLoaderData } from 'react-router';

import Provision from '../../../api/provision';
import Button from '../../../components/Buttons/Button';
import CodeBlock from '../../../components/CodeBlock/CodeBlock';
import DeleteFormModal from '../../../components/DeleteFormModal/DeleteFormModal';
import Modal from '../../../components/Modal';
import OverviewTable from '../../../components/OverviewTable/OverviewTable';
import useApiQuery from '../../../hooks/useApiQuery';
import { useCopyToClipboard } from '../../../hooks/useCopyToClipboard';
import rbacCheck from '../../../rbac/rbacCheck';

import type { OsProjectData } from '@/types/projectTypes';
import type { User } from '@/types/user';

const createConfig = (
  osProject: OsProjectData,
  userEmail: string
): string => `export OS_REGION_NAME=RegionOne
export OS_AUTH_URL=${osProject.cloudId}.in.pan-net.eu:5000/v3
export OS_USER_DOMAIN_NAME=users_domain
export OS_USERNAME=${userEmail}
echo "Please enter your OpenStack Password: "
read -sr OS_PASSWORD_INPUT
export OS_PASSWORD=$OS_PASSWORD_INPUT
export OS_PROJECT_NAME=${osProject.name}
export OS_PROJECT_DOMAIN_NAME=${osProject.juiceGroup}
# set prompt - optional
export BDCNAME=${osProject.cloudId}
export PS1=\\[\\e[32m\\]$OS_USERNAME:$OS_USER_DOMAIN_NAME\\[\\e[m\\]@\\h (\\W)[\\[\\e[34m\\]$OS_PROJECT_NAME:$OS_PROJECT_DOMAIN_NAME\\[\\e[m\\]@\\[\\e[36m\\]\${BDCNAME}\\[\\e[m\\]]\\[\\033[01;31m\\]$(parse_git_branch)\\[\\033[00m\\]\\$
# explicit placement and compute api version - optional
# export OS_PLACEMENT_API_VERSION=1.28
# export OS_COMPUTE_API_VERSION=2.53
# if no DT Cloud Services certs installed on your PC, cert file path should be defined
# export OS_CACERT=<cert_path>`;

const OsProject = (): ReactNode => {
  const intl = useIntl();
  const { productId, juiceGroup, cloudId, osprojectName } = useParams();
  const navigate = useNavigate();
  const userData = useSelector((state: { user: User }) => state.user);
  const copyToClipboard = useCopyToClipboard();

  const loaderOsproject: OsProjectData = useLoaderData();
  const [osProject, setOsProject] = useState<OsProjectData>(loaderOsproject);
  const [osConfigModalOpened, setOsConfigModalOpened] = useState<boolean>(false);
  const [deleteModalOpened, setDeleteModalOpened] = useState<boolean>(false);

  const { makeApiCall: fetchOsProjectDetails } = useApiQuery<OsProjectData>(
    Provision.fetchOsProjectDetails as unknown as (args: unknown) => Promise<OsProjectData>
  );
  const { makeApiCall: deleteOsProjectCall } = useApiQuery<OsProjectData>(
    Provision.deleteOsProject as unknown as (args: unknown) => Promise<OsProjectData>,
    {
      successMessage: 'osProjectDeleted',
    }
  );
  const { makeApiCall: patchOsProjectCall } = useApiQuery<OsProjectData>(
    Provision.patchOsProject as unknown as (args: unknown) => Promise<OsProjectData>,
    {
      successMessage: 'osProjectSetDeleted',
      errorMessage: 'osProjectPatchFailed',
    }
  );

  useEffect(() => {
    const getOsProjectDetails = async (): Promise<void> => {
      const data = await fetchOsProjectDetails(juiceGroup, cloudId, osprojectName);
      if (data) setOsProject(data);
    };

    getOsProjectDetails();
  }, [cloudId, fetchOsProjectDetails, juiceGroup, osprojectName]);

  const deleteOsProject = async (): Promise<void> => {
    setDeleteModalOpened(false);
    const data = await deleteOsProjectCall(cloudId, osprojectName);

    if (data) {
      navigate(-1);
    }
  };

  const patchOsProject = async (): Promise<void> => {
    const data = await patchOsProjectCall(osProject.juiceGroup, osProject.cloudId, osProject.name, {
      status: 'deleted',
    });

    if (data) {
      setDeleteModalOpened(false);
      navigate(-1);
    }
  };

  const config: string = createConfig(osProject, userData.email);

  const canDelete = rbacCheck(userData.roles, '/osprojects:DELETE', {
    productId,
    osProjectType: osProject.type,
    userProducts: userData.productIds,
  });

  let tooltip: string | undefined;
  if (osProject.createdFromJuice) {
    tooltip = 'osProjectDeleteDisabledCreatedFromJuice';
  } else if (!canDelete) {
    tooltip = 'osProjectDeleteDisabled';
  }

  if (!['active', 'deletionFailed'].includes(osProject.status)) return null;

  const rows = [
    {
      title: { id: 'devProjectName' },
      value: osProject.name,
    },
    {
      title: { id: 'openStackUserName' },
      value: osProject.osUser,
    },
    {
      title: { id: 'openStackWorkingSourceFile' },
      component: (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant='outline'
            disabled={!osProject.name}
            onClick={() => {
              setOsConfigModalOpened(true);
            }}
            icon={<ODSCopyPaste />}
            rightIcon
            label={intl.formatMessage({ id: 'copyWorkingSourceFile' })}
          />
        </div>
      ),
    },
    {
      title: { id: 'horizonUrl' },
      component: (
        <Link
          to={osProject.horizonUrl}
          target='_blank'
          rel='noopener noreferrer'
        >
          {osProject.horizonUrl}
        </Link>
      ),
    },
  ];

  return (
    <Container maxWidth='md'>
      {osConfigModalOpened && (
        <Modal
          title={intl.formatMessage({ id: 'configuration' })}
          onClose={() => {
            setOsConfigModalOpened(false);
          }}
          onSubmit={() => {
            setOsConfigModalOpened(false);
            copyToClipboard(config);
          }}
          submitLabel='Copy'
        >
          <CodeBlock
            code={config}
            language='sh'
          />
        </Modal>
      )}
      {deleteModalOpened && (
        <DeleteFormModal
          fieldName='Os Project'
          fieldValue={osProject.name}
          messages={['osProjectsDeleteMessage']}
          onSubmit={() => void deleteOsProject()}
          onCancel={() => {
            setDeleteModalOpened(false);
          }}
        />
      )}
      <OverviewTable
        header={osProject.name}
        rows={rows}
        deleteAction={{
          onClick:
            osProject.status === 'deletionFailed'
              ? patchOsProject
              : () => {
                  setDeleteModalOpened(true);
                },
          label: osProject.status === 'deletionFailed' ? 'Set As Deleted' : '',

          ...(tooltip && {
            tooltip,
          }),
          disabled: !canDelete || !!osProject.createdFromJuice,
        }}
      />
    </Container>
  );
};

export default OsProject;
